// frontend/src/pages/marketplace/FavoritesPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Store, 
  MapPin, 
  Star, 
  Heart, 
  ShoppingBag,
  MessageSquare,
  Shield,
  Clock,
  Trash2,
  Loader,
  ChevronRight,
  TrendingUp,
  ChevronLeft
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import useMarketplaceStore from '../../store/marketplaceStore';
import useAuthStore from '../../store/authStore';
import MessageBusinessModal from '../../components/community/MessageBusinessModal';

const FavoritesPage = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [favorites, setFavorites] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showMessageModal, setShowMessageModal] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0
  });

  const { getFavorites, toggleFavorite } = useMarketplaceStore();

  useEffect(() => {
    loadFavorites();
  }, [pagination.page]);

  const loadFavorites = async () => {
    setIsLoading(true);
    try {
      const response = await getFavorites({
        page: pagination.page,
        limit: pagination.limit
      });
      
      setFavorites(response.favorites || []);
      setPagination(response.pagination || {
        page: 1,
        limit: 20,
        total: response.favorites?.length || 0,
        totalPages: 1
      });
    } catch (error) {
      console.error('Failed to load favorites:', error);
      toast.error('Failed to load saved businesses');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveFavorite = async (businessId, businessName) => {
    try {
      await toggleFavorite(businessId);
      setFavorites(prev => prev.filter(b => b.id !== businessId));
      toast.success(`${businessName} removed from favorites`);
    } catch (error) {
      toast.error('Failed to remove from favorites');
    }
  };

  const handleMessageClick = (business) => {
    if (!user) {
      toast.error('Please login to send messages');
      return;
    }
    setSelectedBusiness(business);
    setShowMessageModal(true);
  };

  const renderStars = (rating) => {
    if (!rating) return null;
    return (
      <div className="flex items-center">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            className={`h-3 w-3 sm:h-4 sm:w-4 ${
              star <= Math.floor(rating)
                ? 'text-yellow-400 fill-current'
                : 'text-gray-300'
            }`}
          />
        ))}
      </div>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Recently';
    const date = new Date(dateString);
    const now = new Date();
    
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const savedDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    
    const diffTime = today - savedDate;
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-5">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="bg-white rounded-xl p-6 sm:p-8 shadow-neomorph">
              <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto" />
              <p className="mt-3 sm:mt-4 text-xs sm:text-sm text-gray-500">Loading your favorites...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#E8ECF5] p-3 sm:p-5">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-[#E8ECF5] rounded-xl p-4 sm:p-5 shadow-neomorph mb-4 sm:mb-5 border border-primary-100/50">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <div>
              <div className="flex items-center space-x-2 mb-1">
                <div className="w-1 h-5 sm:h-6 bg-gradient-to-b from-primary-400 to-primary-600 rounded-full" />
                <h1 className="text-base sm:text-xl font-bold text-gray-800 flex items-center">
                  <Heart className="h-4 w-4 sm:h-5 sm:w-5 mr-1.5 sm:mr-2 text-red-500 fill-current" />
                  Saved Businesses
                </h1>
              </div>
              <p className="text-xs sm:text-sm text-gray-500 ml-3">
                {favorites.length} {favorites.length === 1 ? 'business' : 'businesses'} saved
              </p>
            </div>
            
            <div className="mt-2 sm:mt-0">
              <button
                onClick={() => navigate('/community?tab=marketplace')}
                className="w-full sm:w-auto bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-3 sm:px-4 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-xs sm:text-sm font-medium flex items-center justify-center"
              >
                <Store className="h-3.5 w-3.5 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                Browse Marketplace
              </button>
            </div>
          </div>
        </div>

        {favorites.length === 0 ? (
          <div className="bg-white rounded-xl p-6 sm:p-8 shadow-neomorph border border-gray-100 text-center">
            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-red-100 to-red-200 rounded-xl flex items-center justify-center mx-auto mb-3 sm:mb-4">
              <Heart className="h-8 w-8 sm:h-10 sm:w-10 text-red-500" />
            </div>
            <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-1 sm:mb-2">
              No saved businesses yet
            </h3>
            <p className="text-xs sm:text-sm text-gray-500 mb-4 sm:mb-6 max-w-md mx-auto px-3">
              Start exploring the marketplace and save businesses you're interested in
            </p>
            <button
              onClick={() => navigate('/community?tab=marketplace')}
              className="bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg px-4 sm:px-6 py-2 sm:py-2.5 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-sm font-medium"
            >
              Browse Marketplace
            </button>
          </div>
        ) : (
          <>
            {/* Favorites Grid - Responsive columns */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-5">
              {favorites.map((business) => (
                <div
                  key={business.id}
                  className="bg-white rounded-xl shadow-neomorph border border-gray-100 overflow-hidden hover:shadow-neomorph-hover transition-all"
                >
                  {/* Business Header */}
                  <div className="p-3 sm:p-4 border-b border-gray-100">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-center space-x-2 sm:space-x-3 min-w-0 flex-1">
                        {/* Logo */}
                        <div className="h-10 w-10 sm:h-12 sm:w-12 rounded-xl bg-[#E8ECF5] shadow-neomorph-inset flex items-center justify-center flex-shrink-0">
                          {business.logoUrl ? (
                            <img
                              src={business.logoUrl}
                              alt={business.name}
                              className="h-8 w-8 sm:h-10 sm:w-10 rounded-lg object-cover"
                            />
                          ) : (
                            <Store className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                          )}
                        </div>
                        
                        {/* Basic Info */}
                        <div className="min-w-0 flex-1">
                          <h3 className="font-semibold text-gray-800 text-sm sm:text-base truncate">{business.name}</h3>
                          <div className="flex items-center text-[10px] sm:text-xs text-gray-500 mt-0.5 sm:mt-1">
                            <MapPin className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1 flex-shrink-0" />
                            <span className="truncate">{business.location || business.city || 'Location not set'}</span>
                          </div>
                        </div>
                      </div>

                      {/* Remove Button */}
                      <button
                        onClick={() => handleRemoveFavorite(business.id, business.name)}
                        className="p-1.5 sm:p-2 text-gray-400 hover:text-red-500 bg-[#E8ECF5] rounded-lg shadow-neomorph hover:shadow-neomorph-hover transition-all flex-shrink-0"
                        title="Remove from favorites"
                      >
                        <Trash2 className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                      </button>
                    </div>

                    {/* Verification Badge */}
                    {business.verified && (
                      <div className="mt-2 sm:mt-3 inline-flex items-center px-1.5 sm:px-2 py-0.5 sm:py-1 bg-green-50 text-green-700 text-[10px] sm:text-xs font-medium rounded-lg border border-green-200 shadow-neomorph-inset">
                        <Shield className="h-2.5 w-2.5 sm:h-3 sm:w-3 mr-0.5 sm:mr-1" />
                        Verified
                      </div>
                    )}
                  </div>

                  {/* Business Content */}
                  <div className="p-3 sm:p-4">
                    {/* Description */}
                    <p className="text-[10px] sm:text-xs text-gray-500 mb-2 sm:mb-3 line-clamp-2 bg-[#E8ECF5] p-1.5 sm:p-2 rounded-lg shadow-neomorph-inset">
                      {business.description || 'No description provided'}
                    </p>

                    {/* Rating */}
                    <div className="flex flex-wrap items-center justify-between gap-1 mb-2 sm:mb-3">
                      <div className="flex items-center">
                        <div className="bg-yellow-50 rounded-lg p-0.5 sm:p-1 shadow-neomorph-inset mr-1.5 sm:mr-2">
                          {renderStars(business.rating || 0)}
                        </div>
                        <span className="text-[10px] sm:text-xs font-medium text-gray-700">
                          {business.rating?.toFixed(1) || '0.0'}
                        </span>
                      </div>
                      <span className="text-[10px] sm:text-xs text-gray-500 bg-[#E8ECF5] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg shadow-neomorph-inset">
                        ({business.reviewCount || 0} reviews)
                      </span>
                    </div>

                    {/* Stats */}
                    <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 text-[10px] sm:text-xs text-gray-600 mb-3 sm:mb-4">
                      <div className="flex items-center bg-[#E8ECF5] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg shadow-neomorph-inset">
                        <ShoppingBag className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1 text-primary-500" />
                        <span>{business._count?.products || 0} products</span>
                      </div>
                      <div className="flex items-center bg-[#E8ECF5] px-1.5 sm:px-2 py-0.5 sm:py-1 rounded-lg shadow-neomorph-inset">
                        <Clock className="h-2.5 w-2.5 sm:h-3.5 sm:w-3.5 mr-0.5 sm:mr-1 text-gray-500" />
                        <span>{formatDate(business.savedAt)}</span>
                      </div>
                    </div>

                    {/* Category */}
                    <div className="mb-3 sm:mb-4">
                      <span className="inline-block px-2 sm:px-3 py-0.5 sm:py-1 bg-primary-50 text-primary-700 text-[10px] sm:text-xs font-medium rounded-lg border border-primary-200 shadow-neomorph-inset">
                        {business.category}
                      </span>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex flex-col sm:flex-row gap-2">
                      <button
                        onClick={() => handleMessageClick(business)}
                        className="order-2 sm:order-1 w-full sm:flex-1 bg-white rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 shadow-neomorph hover:shadow-neomorph-hover transition-all text-[10px] sm:text-xs font-medium text-gray-700 flex items-center justify-center border border-gray-100"
                      >
                        <MessageSquare className="h-3 w-3 sm:h-3.5 sm:w-3.5 mr-1 sm:mr-1.5" />
                        Message
                      </button>
                      <button
                        onClick={() => navigate(`/community/marketplace/business/${business.id}`)}
                        className="order-1 sm:order-2 w-full sm:flex-1 bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg px-2 sm:px-3 py-1.5 sm:py-2 shadow-md shadow-primary-500/30 hover:shadow-lg hover:shadow-primary-500/40 transition-all text-[10px] sm:text-xs font-medium text-white flex items-center justify-center"
                      >
                        View Details
                        <ChevronRight className="h-3 w-3 sm:h-3.5 sm:w-3.5 ml-0.5 sm:ml-1" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination - Responsive */}
            {pagination.totalPages > 1 && (
              <div className="mt-6 sm:mt-8 flex justify-center">
                <nav className="flex items-center space-x-1 sm:space-x-2 bg-white rounded-lg p-1 shadow-neomorph">
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                    disabled={pagination.page === 1}
                    className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E8ECF5] transition-all"
                  >
                    <ChevronLeft className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                  <span className="px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-medium text-gray-700 bg-[#E8ECF5] rounded-lg shadow-neomorph-inset">
                    <span className="hidden sm:inline">Page </span>
                    {pagination.page} / {pagination.totalPages}
                  </span>
                  <button
                    onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                    disabled={pagination.page === pagination.totalPages}
                    className="px-2 sm:px-3 py-1.5 sm:py-2 rounded-lg text-xs sm:text-sm font-medium text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[#E8ECF5] transition-all"
                  >
                    <ChevronRight className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
                  </button>
                </nav>
              </div>
            )}
          </>
        )}

        {/* Message Modal */}
        {showMessageModal && selectedBusiness && (
          <MessageBusinessModal
            business={selectedBusiness}
            onClose={() => {
              setShowMessageModal(false);
              setSelectedBusiness(null);
            }}
            onSuccess={() => {
              toast.success('Message sent successfully!');
            }}
          />
        )}
      </div>

      <style jsx>{`
        .shadow-neomorph {
          box-shadow: 6px 6px 11px #d0d6e0, -6px -6px 11px #ffffff;
        }
        .shadow-neomorph-hover {
          box-shadow: 4px 4px 7px #d0d6e0, -4px -4px 7px #ffffff;
        }
        .shadow-neomorph-inset {
          box-shadow: inset 2.5px 2.5px 5px #d0d6e0, inset -2.5px -2.5px 5px #ffffff;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
    </div>
  );
};

export default FavoritesPage;